// S-063-15 / DEC-062: calendar_days 生テキスト保管リポジトリ（§4.1）。
//
// users/{uid}/calendar_days/{YYYY-MM-DD} に「1 日分の予定生テキスト」を保管する。
// docID = JST 日付なので、同日再取込は同 docID への上書き（merge なし＝完全置換）になり、
// 旧構造化方式の重複検出（splitReimport / overwriteEvents）が構造的に不要化する（GATE-D）。
//
// rules（firestore.rules:196）は hasOnly(['rawText','updatedAt']) / docID=YYYY-MM-DD /
//   rawText 1〜32000 字を強制する。本 repo は保存前に必ず PII マスキング＋字数 truncate する。
//
// やらないこと: 日付分割（calendarRawSplit）、マッチング/Gemini 注入（functions・S-063-16/17）。

import {
  collection,
  deleteDoc,
  doc,
  documentId,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  Unsubscribe,
} from 'firebase/firestore';
import { db } from '../firebase';
import { maskPii } from './calendarRawSplit';

// rules と一致させる字数上限（保存前に truncate）。
export const MAX_CALENDAR_DAY_CHARS = 32000;

// docID の形式（JST 日付）。rules も同じ正規表現を強制するため、不正 docID はクライアントで先に弾く。
const DAY_ID_RE = /^\d{4}-\d{2}-\d{2}$/;

// Firestore に保存される 1 日分（rawText + updatedAt のみ）。
export interface CalendarDay {
  dayId: string;
  rawText: string;
  updatedAt?: Timestamp;
}

function daysCol(uid: string) {
  return collection(db, 'users', uid, 'calendar_days');
}

// 1 日分の生テキストを保存（同日は完全上書き＝重複検出不要）。
//   - 保存前に PII マスキング（RT6）＋字数 truncate（rules 32000 字上限）。
//   - マスク後が空（空白のみ）なら書込まず false を返す（rules の size()>0 違反を避ける）。
//   返り値: 実際に書き込んだら true。
export async function saveDay(uid: string, dayId: string, rawText: string): Promise<boolean> {
  if (!DAY_ID_RE.test(dayId)) return false;
  const masked = maskPii(rawText).slice(0, MAX_CALENDAR_DAY_CHARS);
  if (!masked.trim()) return false;
  await setDoc(doc(db, 'users', uid, 'calendar_days', dayId), {
    rawText: masked,
    updatedAt: serverTimestamp(),
  }); // merge なし＝完全上書き（同日再取込は置換）
  return true;
}

// 複数日分を保存し、実際に保存できた dayId 群を返す（遡及対象の起点・S-063-17）。
export async function saveDays(
  uid: string,
  days: { dayId: string; rawText: string }[],
): Promise<string[]> {
  const saved: string[] = [];
  for (const d of days) {
    const ok = await saveDay(uid, d.dayId, d.rawText);
    if (ok) saved.push(d.dayId);
  }
  return saved;
}

// 保存済みの日付別生テキストを購読（docID 降順＝新しい日付が上）。
export function subscribeDays(uid: string, cb: (days: CalendarDay[]) => void): Unsubscribe {
  return onSnapshot(
    query(daysCol(uid), orderBy(documentId(), 'desc')),
    (snap) => {
      cb(
        snap.docs.map((d) => {
          const data = d.data() as any;
          return { dayId: d.id, rawText: data.rawText || '', updatedAt: data.updatedAt };
        }),
      );
    },
    (e) => {
      console.error('[calendarDaysRepo] subscribeDays failed:', e);
    },
  );
}

// 1 日分を削除。
export async function deleteDay(uid: string, dayId: string): Promise<void> {
  if (!dayId) return;
  await deleteDoc(doc(db, 'users', uid, 'calendar_days', dayId));
}

// 保存済みの予定をすべて削除（プライバシー配慮・ユーザー操作）。
export async function deleteAllDays(uid: string): Promise<void> {
  const snap = await getDocs(daysCol(uid));
  await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));
}
